import { AfterViewInit, ChangeDetectionStrategy, Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild, ViewEncapsulation } from '@angular/core';
import { MatTabChangeEvent } from '@angular/material/tabs';
import { PageDataType } from '@common/paged-data/enums/page-data-type.enum';
import { PageFilterGroupType } from '@common/paged-data/enums/page-filter-group-type.enum';
import { ErrorService } from '@portal-core/errors/services/error.service';
import { LicenseUserSeatType } from '@portal-core/license-users/enums/license-user-seat-type.enum';
import { LicenseUserStatus } from '@portal-core/license-users/enums/license-user-status.enum';
import { LicenseUsersService } from '@portal-core/license-users/services/license-users.service';
import { CentralPermissions } from '@portal-core/permissions/enums/central-permissions.enum';
import { PermissionsService } from '@portal-core/permissions/services/permissions.service';
import { ProjectLicenseUsersListComponent } from '@portal-core/projects/components/project-license-users-list/project-license-users-list.component';
import { ProjectTeamsListComponent } from '@portal-core/projects/components/project-teams-list/project-teams-list.component';
import { Project } from '@portal-core/projects/models/project.model';
import { ProjectsService } from '@portal-core/projects/services/projects.service';
import { TeamsService } from '@portal-core/teams/services/teams.service';
import { AutocompleteInputComponent } from '@portal-core/ui/autocomplete/components/autocomplete-input/autocomplete-input.component';
import { PageFilterService } from '@portal-core/ui/page-filters/services/page-filter.service';
import { PopupComponent } from '@portal-core/ui/popup/components/popup/popup.component';
import { PopupTriggerDirective } from '@portal-core/ui/popup/directives/popup-trigger/popup-trigger.directive';
import { LoadingState } from '@portal-core/util/loading-state';
import { Observable, Subscription } from 'rxjs';

export enum ProjectAccessFormTab {
  Teams = 0,
  Users
}

@Component({
  selector: 'mc-project-access-form',
  templateUrl: './project-access-form.component.html',
  styleUrls: ['./project-access-form.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ProjectAccessFormComponent implements OnInit, AfterViewInit, OnDestroy {
  LicenseUserSeatType: typeof LicenseUserSeatType = LicenseUserSeatType;

  @Input() selectedTabIndex?: ProjectAccessFormTab;
  @Input() project: Project;

  @Output() saved: EventEmitter<void> = new EventEmitter<void>();

  @ViewChild(ProjectTeamsListComponent, { static: false }) projectTeamsListComponent?: ProjectTeamsListComponent;
  @ViewChild('projectTeamsAutoComplete', { static: false }) projectTeamsAutocomplete: AutocompleteInputComponent;
  @ViewChild('popupTriggerTeams', { static: false, read: PopupTriggerDirective }) popupTriggerTeams: PopupTriggerDirective;
  @ViewChild('addProjectTeamsPopup', { static: false }) addProjectTeamsPopup: PopupComponent;
  @ViewChild('filterProjectTeams', { static: false }) filterProjectTeams: ElementRef;
  projectTeamsFilterName: string = 'project-teams';
  filterProjectTeamsSubscription: Subscription;

  @ViewChild(ProjectLicenseUsersListComponent, { static: false }) projectLicenseUsersListComponent?: ProjectLicenseUsersListComponent;
  @ViewChild('projectLicenseUsersAutoComplete', { static: false }) projectLicenseUsersAutocomplete: AutocompleteInputComponent;
  @ViewChild('popupTriggerUsers', { static: false, read: PopupTriggerDirective }) popupTriggerUsers: PopupTriggerDirective;
  @ViewChild('addProjectLicenseUsersPopup', { static: false }) addProjectLicenseUsersPopup: PopupComponent;
  @ViewChild('filterProjectLicenseUsers', { static: false }) filterProjectLicenseUsers: ElementRef;
  projectLicenseUsersFilterName: string = 'project-license-users';
  filterProjectLicenseUsersSubscription: Subscription;

  savingState: LoadingState<string> = new LoadingState<string>();
  canEdit$: Observable<boolean>;

  constructor(
    private projectsService: ProjectsService,
    private licenseUsersService: LicenseUsersService,
    private teamsService: TeamsService,
    private permissionsService: PermissionsService,
    private errorService: ErrorService,
    private pageFilterService: PageFilterService
  ) { }

  ngOnInit() {
    this.canEdit$ = this.permissionsService.currentUserHasPermission$(CentralPermissions.ManageTeamsProjects);
  }

  ngAfterViewInit(): void {
    this.initFilterSubscriptions();

    // set license user filter to only ever show certain user statuses
    const licenseUserStatusFilterBuilder = this.pageFilterService.create({
      Id: `${this.projectLicenseUsersFilterName}-status`,
      Type: PageFilterGroupType.Custom
    }).select('SeatType', [LicenseUserSeatType.Author]).select('Status', [LicenseUserStatus.Active, LicenseUserStatus.Invited, LicenseUserStatus.Paused]);
    this.licenseUsersService.setPagedDataListFilterByName$(this.projectLicenseUsersListComponent.dataListId, `${this.projectLicenseUsersFilterName}-status`, licenseUserStatusFilterBuilder.value);
    this.licenseUsersService.setPagedDataListFilterByName$(this.projectLicenseUsersAutocomplete.selectList.dataListId, `${this.projectLicenseUsersFilterName}-status`, licenseUserStatusFilterBuilder.value);

    // set default license user order
    this.licenseUsersService.setPagedDataListOrder$(this.projectLicenseUsersListComponent.dataListId, 'User.FullName', 'asc');
    this.licenseUsersService.setPagedDataListOrder$(this.projectLicenseUsersAutocomplete.selectList.dataListId, 'User.FullName', 'asc');

    // set default team order
    this.teamsService.setPagedDataListOrder$(this.projectTeamsListComponent.dataListId, 'Name', 'asc');
    this.teamsService.setPagedDataListOrder$(this.projectTeamsAutocomplete.selectList.dataListId, 'Name', 'asc');
  }

  ngOnDestroy(): void {
    this.filterProjectTeamsSubscription?.unsubscribe();
    this.filterProjectTeamsSubscription = null;
    this.filterProjectLicenseUsersSubscription?.unsubscribe();
    this.filterProjectLicenseUsersSubscription = null;
  }

  onSaved() {
    this.saved.emit();
  }

  onTabChange(event: MatTabChangeEvent) {
    this.initFilterSubscriptions();

    switch (event.tab.textLabel) {
      case 'Teams':
        this.projectTeamsListComponent.checkViewportSize();
        break;
      case 'Users':
        this.projectLicenseUsersListComponent.checkViewportSize();
        break;
    }
  }

  initFilterSubscriptions() {
    if (!this.filterProjectTeamsSubscription) {
      this.filterProjectTeamsSubscription = this.projectTeamsListComponent?.getFilter$(this.projectTeamsFilterName).subscribe(filter => {
        if (!filter) {
          this.filterProjectTeams.nativeElement.value = '';
        }
      });
    }
    if (!this.filterProjectLicenseUsersSubscription) {
      this.filterProjectLicenseUsersSubscription = this.projectLicenseUsersListComponent?.getFilter$(this.projectLicenseUsersFilterName).subscribe(filter => {
        if (!filter) {
          this.filterProjectLicenseUsers.nativeElement.value = '';
        }
      });
    }
  }

  onAddProjectTeamsClicked() {
    if (this.projectTeamsAutocomplete.value) {
      this.savingState.update(true);

      this.projectsService.addTeamsToProject$(this.project.Id, this.projectTeamsAutocomplete.value as number[]).subscribe(() => {
        this.projectTeamsListComponent.hardReload();
        this.projectTeamsAutocomplete.close();
        this.popupTriggerTeams.close();
        this.savingState.update(false);
        this.saved.emit();
      }, error => {
        this.savingState.update(false, 'There was an error adding teams to the project.', this.errorService.getErrorMessages(error));
      });
    }
  }

  onCancelProjectTeamsClicked() {
    this.savingState.update(false);
    this.addProjectTeamsPopup.close();
  }

  onProjectTeamsPopupClosed() {
    this.projectTeamsAutocomplete.value = null;
  }

  onFilterProjectTeamsKeyUp(event: any) {
    const fb = this.pageFilterService.create({
      Id: this.projectTeamsFilterName,
      Type: PageFilterGroupType.Search
    });
    fb.search(['Name'], event.target.value, [
      { PropertyName: 'Name', PropertyType: PageDataType.String }
    ]);
    this.teamsService.setPagedDataListFilterByName$(this.projectTeamsListComponent.dataListId, this.projectTeamsFilterName, fb.value);
  }

  onAddProjectLicenseUsersClicked() {
    if (this.projectLicenseUsersAutocomplete.value) {
      this.savingState.update(true);

      this.projectsService.addLicenseUsersToProject$(this.project.Id, this.projectLicenseUsersAutocomplete.value as number[]).subscribe(() => {
        this.projectLicenseUsersListComponent.hardReload();
        this.projectLicenseUsersAutocomplete.close();
        this.popupTriggerUsers.close();
        this.savingState.update(false);
        this.saved.emit();
      }, error => {
        this.savingState.update(false, 'There was an error adding users to the project.', this.errorService.getErrorMessages(error));
      });
    }
  }

  onCancelProjectLicenseUsersClicked() {
    this.savingState.update(false);
    this.addProjectLicenseUsersPopup.close();
  }

  onProjectLicenseUsersPopupClosed() {
    this.projectLicenseUsersAutocomplete.value = null;
  }

  onFilterProjectLicenseUsersKeyUp(event: any) {
    const fb = this.pageFilterService.create({
      Id: this.projectLicenseUsersFilterName,
      Type: PageFilterGroupType.Search
    });
    fb.search(['User.Email', 'User.FullName'], event.target.value, [
      { PropertyName: 'User.Email', PropertyType: PageDataType.String },
      { PropertyName: 'User.FullName', PropertyType: PageDataType.String }
    ]);
    this.licenseUsersService.setPagedDataListFilterByName$(this.projectLicenseUsersListComponent.dataListId, this.projectLicenseUsersFilterName, fb.value);
  }
}
